import { CSSObject } from '@emotion/react'
import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Display,
  Heading2,
  JustifyContent,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { ContentstackImage } from '../ContentstackImage'
import { CtaLink, mapCmsCta } from '../../cta'
import type { CmsCtaIntentHandlers } from '../../cta'
import type { RightAlignedImageWithTextFields } from './types'

export type RightAlignedImageWithTextProps = RightAlignedImageWithTextFields & {
  ctaIntentHandlers?: CmsCtaIntentHandlers
}

const Container = withCitrusTheme(Box, (theme) => ({
  ...(theme.responsiveStyle(['column', null, null, 'row'], ['flexDirection']) as CSSObject),
  margin: '0 auto',
}))

function getBodyParagraphs(body?: string) {
  if (!body) {
    return []
  }

  return body
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

export function RightAlignedImageWithText({
  heading,
  body,
  image,
  image_mobile,
  image_alt,
  cta,
  ctaIntentHandlers,
}: RightAlignedImageWithTextProps) {
  const bodyParagraphs = getBodyParagraphs(body)
  const ctaLink = mapCmsCta(cta, ctaIntentHandlers)

  return (
    <Container
      display={Display.Flex}
      alignItems={AlignItems.FlexStart}
      justifyContent={JustifyContent.SpaceEvenly}
      paddingH={8}
      paddingV={16}
      gap={[8, null, null, 16]}
      maxWidth="1200px"
    >
      <Box width={['100%', null, null, '50%']} paddingRight={[null, null, null, 4]}>
        <Heading2>{heading}</Heading2>

        {bodyParagraphs.length > 0 && (
          <>
            <Space size={4} />
            {bodyParagraphs.map((paragraph, index) => (
              <React.Fragment key={paragraph}>
                {index > 0 && <Space size={4} />}
                <Body2 size={4}>{paragraph}</Body2>
              </React.Fragment>
            ))}
          </>
        )}

        {ctaLink && (
          <>
            <Space size={6} />
            <Box
              display={Display.Flex}
              justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
            >
              <CtaLink cta={ctaLink} />
            </Box>
          </>
        )}
      </Box>

      {image?.url && (
        <Box
          width={['100%', null, null, '50%']}
          style={{ borderRadius: '8px', overflow: 'hidden' }}
        >
          <ContentstackImage
            fit="cover"
            width="100%"
            sizes="(min-width: 1024px) 50vw, 100vw"
            alt={image_alt || ''}
            src={image.url}
            mobileSrc={image_mobile?.url}
            mobileMedia="(max-width: 1023px)"
          />
        </Box>
      )}
    </Container>
  )
}
